import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { ButtonView, ImageScaleView, animate } from '@play-co/timestep-core/ui';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature } from 'src/replicant/analytics';
import { waitForItPromise } from 'src/lib/utils';
import { downscale } from 'src/conf/scale';
import StateObserver from 'src/StateObserver';
import sounds from 'src/lib/sounds';

type CloseHandler = () => void;

export default class PopupWeeklyLeaderboardInfo extends PopupBasicLazy {
  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = downscale(1080);
    const height = downscale(1920);

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width,
      height,
      close: () => opts.close(),
      tracking: {
        feature: Feature.WEEKLY_LEADERBOARD._,
        $subFeature: Feature.WEEKLY_LEADERBOARD.POPUP_INFO,
      },
      zIndex: 10000,
    });

    this.buttonClose.hide();

    // This color is the same as backgroundDarker in uiConfig.popups, but less transparent
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.9)',
    });

    // Screen tap area
    new ButtonView({
      superview: this.box,
      width: width,
      height: height,
      zIndex: 1,
      onClick: () => {
        sounds.sfx.play('ok');
        opts.close();
      },
    });
  }

  public getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs([
        'assets/ui/weeklyLeaderboard/popups/popupInfo',
      ]),
      ...AssetGroup.constructURLs(['assets/ui/weeklyLeaderboard/shared']),
    ]);
  }

  public async fadeIn() {
    await super.fadeIn();

    //Animate in all items
    await waitForItPromise(300);

    sounds.sfx.play('win');

    const locale = StateObserver.getLocale();
    const en = locale === 'en';

    const title = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/weeklyLeaderboard/shared/title_overlay_${locale}.png`,
      width: downscale(en ? 627 : 524),
      height: downscale(en ? 98 : 100),
      scaleMethod: 'contain',
      x: this.box.style.width * 0.5,
      y: 115,
      centerOnOrigin: true,
    });
    await this.showAnimated(title);

    const howToPlay = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/weeklyLeaderboard/popups/popupInfo/how_to_play_${locale}.png`,
      width: downscale(en ? 288 : 133),
      height: downscale(en ? 61 : 58),
      scaleMethod: 'contain',
      x: this.box.style.width * 0.5,
      y: 175,
      centerOnOrigin: true,
    });
    await this.showAnimated(howToPlay);

    const item1 = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/weeklyLeaderboard/popups/popupInfo/item_1.png`,
      width: downscale(293),
      height: downscale(303),
      scaleMethod: 'contain',
      x: 195,
      y: 350,
      centerOnOrigin: true,
    });
    new ImageScaleView({
      superview: item1,
      image: `assets/ui/weeklyLeaderboard/popups/popupInfo/text_1_${locale}.png`,
      width: downscale(en ? 492 : 317),
      height: downscale(en ? 115 : 107),
      scaleMethod: 'contain',
      x: item1.style.width * 0.5 - 10,
      y: 250,
      centerOnOrigin: true,
    });
    await this.showAnimated(item1);

    const arrow1 = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/weeklyLeaderboard/popups/popupInfo/arrow.png`,
      width: downscale(119),
      height: downscale(121),
      scaleMethod: 'contain',
      x: 380,
      y: 450,
      centerOnOrigin: true,
    });
    await this.showAnimated(arrow1);

    const item2 = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/weeklyLeaderboard/popups/popupInfo/item_2.png`,
      width: downscale(317),
      height: downscale(300),
      scaleMethod: 'contain',
      x: 520,
      y: 530,
      centerOnOrigin: true,
    });
    new ImageScaleView({
      superview: item2,
      image: `assets/ui/weeklyLeaderboard/popups/popupInfo/text_2_${locale}.png`,
      width: downscale(en ? 324 : 245),
      height: downscale(en ? 53 : 58),
      scaleMethod: 'contain',
      x: item2.style.width * 0.5,
      y: 250,
      centerOnOrigin: true,
    });
    await this.showAnimated(item2);

    const arrow2 = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/weeklyLeaderboard/popups/popupInfo/arrow.png`,
      width: downscale(119),
      height: downscale(121),
      scaleMethod: 'contain',
      flipX: true,
      x: 340,
      y: 660,
      centerOnOrigin: true,
    });
    await this.showAnimated(arrow2);

    const item3 = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/weeklyLeaderboard/popups/popupInfo/item_3.png`,
      width: downscale(369),
      height: downscale(276),
      scaleMethod: 'contain',
      x: 170,
      y: 720,
      centerOnOrigin: true,
    });
    new ImageScaleView({
      superview: item3,
      image: `assets/ui/weeklyLeaderboard/popups/popupInfo/text_3_${locale}.png`,
      width: downscale(en ? 306 : 241),
      height: downscale(en ? 52 : 58),
      scaleMethod: 'contain',
      x: item3.style.width * 0.5 + 10,
      y: 210,
      centerOnOrigin: true,
    });
    await this.showAnimated(item3);

    const footerMessage = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/weeklyLeaderboard/popups/popupInfo/footer_${locale}.png`,
      width: downscale(929),
      height: downscale(278),
      scaleMethod: 'contain',
      x: this.box.style.width * 0.5,
      y: 950,
      centerOnOrigin: true,
    });
    await this.showAnimated(footerMessage);

    const tapToContinue = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/weeklyLeaderboard/shared/tap_to_continue_${locale}.png`,
      width: downscale(en ? 569 : 433),
      height: downscale(en ? 87 : 84),
      scaleMethod: 'contain',
      x: this.box.style.width * 0.5,
      y: 1150,
      centerOnOrigin: true,
    });
    await this.showAnimated(tapToContinue);
  }

  private async showAnimated(view: View) {
    view.updateOpts({ scale: 0 });
    animate(view).then({ scale: 1 }, 300, animate.easeOutBack);
    sounds.sfx.play('puzzle-ice-tap');
    await waitForItPromise(200);
  }
}
